import { useState } from 'react'
import { Plus, FolderTree, Edit, Trash2, ChevronRight } from 'lucide-react'
import { Button, Badge, Card, CardHeader, CardBody, Modal } from '../../components/ui'
import type { StoreGroup } from '../../types'

// Mock data
const mockGroups: StoreGroup[] = [
  { id: 'g1', name: '関東地区', description: '関東1都6県の店舗', groupType: 'region', storeCount: 156, createdAt: '' },
  { id: 'g2', name: '関西地区', description: '関西2府4県の店舗', groupType: 'region', storeCount: 89, createdAt: '' },
  { id: 'g3', name: '中部地区', description: '中部地方の店舗', groupType: 'region', storeCount: 45, createdAt: '' },
  { id: 'g4', name: '九州地区', description: '九州・沖縄地方の店舗', groupType: 'region', storeCount: 34, createdAt: '' },
  { id: 'g5', name: '北海道', description: '北海道の店舗', groupType: 'region', storeCount: 12, createdAt: '' },
  { id: 'g6', name: '大型店舗', description: '座席数100以上の大型店舗', groupType: 'size', storeCount: 78, createdAt: '' },
  { id: 'g7', name: '中型店舗', description: '座席数50-99の中型店舗', groupType: 'size', storeCount: 189, createdAt: '' },
  { id: 'g8', name: '小型店舗', description: '座席数50未満の小型店舗', groupType: 'size', storeCount: 89, createdAt: '' },
  { id: 'g9', name: 'プレミアム契約', description: 'プレミアム契約店舗', groupType: 'contract', storeCount: 23, createdAt: '' },
  { id: 'g10', name: 'スタンダード契約', description: 'スタンダード契約店舗', groupType: 'contract', storeCount: 267, createdAt: '' },
]

const groupTypeConfig = {
  region: { label: '地域別', color: 'bg-blue-500' },
  size: { label: '店舗規模別', color: 'bg-green-500' },
  contract: { label: '契約種別', color: 'bg-purple-500' },
}

export default function StoreGroupListPage() {
  const [selectedGroup, setSelectedGroup] = useState<StoreGroup | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const groupedByType = mockGroups.reduce((acc, group) => {
    if (!acc[group.groupType]) acc[group.groupType] = []
    acc[group.groupType].push(group)
    return acc
  }, {} as Record<string, StoreGroup[]>)

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">店舗グループ管理</h1>
          <p className="text-sm text-gray-500 mt-1">店舗のグループ分けを管理</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreateModal(true)}>
          新規グループ作成
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Group List */}
        <div className="lg:col-span-1 space-y-4">
          {Object.entries(groupedByType).map(([type, groups]) => {
            const config = groupTypeConfig[type as keyof typeof groupTypeConfig]
            return (
              <Card key={type}>
                <CardHeader className="py-3">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${config.color} mr-2`} />
                    <h3 className="font-semibold text-gray-900">{config.label}</h3>
                  </div>
                </CardHeader>
                <div className="divide-y divide-gray-100">
                  {groups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => setSelectedGroup(group)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                        selectedGroup?.id === group.id ? 'bg-primary-50' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <FolderTree className="w-4 h-4 text-gray-400 mr-3" />
                        <div>
                          <p className="font-medium text-gray-900">{group.name}</p>
                          <p className="text-xs text-gray-500">{group.storeCount}店舗</p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                </div>
              </Card>
            )
          })}
        </div>

        {/* Group Detail */}
        <div className="lg:col-span-2">
          {selectedGroup ? (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedGroup.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedGroup.description}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" leftIcon={<Edit className="w-4 h-4" />}>
                    編集
                  </Button>
                  <Button variant="danger" size="sm" leftIcon={<Trash2 className="w-4 h-4" />}>
                    削除
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <div className="mb-4">
                  <Badge variant="primary">
                    {groupTypeConfig[selectedGroup.groupType as keyof typeof groupTypeConfig].label}
                  </Badge>
                  <span className="ml-3 text-gray-600">{selectedGroup.storeCount}店舗が所属</span>
                </div>

                <h4 className="font-medium text-gray-900 mb-3">所属店舗</h4>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                  {[...Array(Math.min(selectedGroup.storeCount || 0, 10))].map((_, i) => (
                    <div key={i} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium text-gray-900">サンプル店舗 {i + 1}</p>
                        <p className="text-sm text-gray-500">SHP00{i + 1} - 東京都渋谷区</p>
                      </div>
                      <Button variant="ghost" size="sm">
                        削除
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex gap-2">
                  <Button variant="secondary">店舗を追加</Button>
                </div>
              </CardBody>
            </Card>
          ) : (
            <Card>
              <CardBody className="text-center py-12">
                <FolderTree className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">左側のリストからグループを選択してください</p>
              </CardBody>
            </Card>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="新規グループ作成"
      >
        <form className="space-y-4">
          <div>
            <label className="form-label">グループ名</label>
            <input type="text" className="form-input" placeholder="グループ名を入力" />
          </div>
          <div>
            <label className="form-label">種別</label>
            <select className="form-select">
              <option value="region">地域別</option>
              <option value="size">店舗規模別</option>
              <option value="contract">契約種別</option>
            </select>
          </div>
          <div>
            <label className="form-label">説明</label>
            <textarea className="form-textarea" rows={3} placeholder="グループの説明（任意）" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              キャンセル
            </Button>
            <Button type="submit">作成</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

